/**
 * The arithmetic behind the TAX_SIMULATION card's sliders.
 *
 * Kept OUT of `TaxSummaryCardV2.vue` for the same reason `taxCardFormat.js` is: a template
 * cannot be unit-tested, and every function here decides something a customer reads as a
 * number. Pinned by `tests/taxSimulationCard.test.mjs`.
 *
 * 🔴 **What is and is not a tax rule here (2026-09-08 redesign).** Until the redesign this file
 * summed what the customer dragged and compared it against ceilings the ADMIN authored for
 * drawing, and nothing in it produced a tax figure. The redesign changes the contract in one
 * deliberate way: the server now SHIPS the bracket ladder and the net income before tax-saving
 * products (`picker.tax`), so the client can PREVIEW the saving live while a bar is being
 * dragged — `taxFromBrackets` / `taxSaved` below are that preview and nothing more. The
 * authoritative figures still come back from the server on submit (the SIM_RESULT card, costed
 * by the calc formulas). Every ceiling on the wire (`max`, `cap`) is still a drawing
 * instruction the admin authored; the formulas apply the real ones on the simulated run.
 *
 * One slider on the wire is `{ id, label, max, min, value, step, sliderMax?, hidden? }`:
 *   `id`        — the deduction's real FieldKey, and the key a submission echoes back
 *                 (`formSubmit.values`, TaxPlanningV2-CardPostback-Wire-Design.md §1.4/§2.3). It
 *                 is therefore also what every state map in this screen is filed under. NOT the
 *                 label, which repeats across groups, and not an index, which collides.
 *   `max`       — the deduction ceiling the admin authored: the dashed cap line on the bar.
 *                 Baht past it are drawn as the striped over-zone — buyable, not deductible.
 *   `min`       — the deduction the customer ALREADY holds ("ซื้อแล้ว"), and the START of the
 *                 draggable range: they may add to it on this screen, they cannot give it back.
 *   `value`     — the position the server last costed. Equal to `min` on arrival, because the
 *                 card is rebuilt from the form's own amounts once per turn.
 *   `sliderMax` — the absolute END of the draggable range. Absent ⇒ the range ends at the
 *                 ceiling, which is how the card behaved before the redesign (drag stops at
 *                 `max`); present ⇒ the customer may drag PAST the ceiling into the over-zone
 *                 up to it, which is what lets the card say "buying more here saves nothing".
 *   `hidden`    — the bar is not DRAWN but its holding still counts against the group ceiling
 *                 (a provident fund the customer cannot top up here but which eats headroom).
 *
 * 🔴 `min` CAN SIT ABOVE `max`, and that is not corrupt data. `max` is what the law grants;
 * `min` is what the customer actually bought. Someone holding 18,000 of parental health cover
 * against a 15,000 ceiling arrives as `min = value = 18000, max = 15000` — a row that is
 * un-draggable and already over its ceiling at once. Nothing in this file may reconcile the
 * two by moving either number: the floor is a fact about the customer, the ceiling is a fact
 * about the law, and the gap between them is precisely what the "เกินสิทธิ์" badge says.
 *
 * 🔴 The app's card contract (`type4` → `TaxSimItem`) does not list `id`, and that is not a
 * disagreement: it documents what the phone DRAWS, so the phone drops the key silently. The
 * key belongs to the other contract — what a client SENDS BACK — and the surfaces that post
 * `values` (this preview, the playground) are the ones that need it.
 *
 * `local` throughout is `{ [sliderId]: number }` — where the customer has dragged each
 * control since the current card arrived. A slider absent from it has not been touched, so
 * it reads from the card's own `value`.
 */

/** The visual headroom the design draws past the tallest figure on a bar — `picker.scaleHeadroom`. */
export const DEFAULT_SCALE_HEADROOM = 1.25;
/** The headroom kept past the thumb while dragging — `picker.dragHeadroom`. */
export const DEFAULT_DRAG_HEADROOM = 1.05;
/** The group bar's own headroom past `max(cap, rawAll)` — fixed in the design (1.02). */
export const GROUP_BAR_HEADROOM = 1.02;

/** The three stat tiles the design draws, by the `key` the payload names them with. */
export const STAT_DEDUCTIBLE = 'DEDUCTIBLE';
export const STAT_NEW_MONEY = 'NEW_MONEY';
export const STAT_TAX_SAVED = 'TAX_SAVED';

/** The badge captions the design falls back to when `picker.badge` authored none. */
export const DEFAULT_BADGE = {
  remainingLabel: 'เหลือสิทธิลดหย่อน {amount}',
  fullLabel: 'เต็มสิทธิ์แล้ว',
  overLabel: 'เกินสิทธิ์ {amount}',
};

/**
 * A group's key: its authored `id` when it has one, else its position on the card.
 *
 * The authored id is what an item's `relation_cap` names, so it has to win; `@index` is the
 * fallback that lets an unnamed group still own a bar and a slot in `allocate` (it can
 * neither lend nor borrow, because nothing can name it).
 *
 * 🔴 EXPORTED because it used to be written three times — twice inside `allocate`, once more
 * in the card component — and every bar, cap caption and lent slice is looked up by whatever
 * it returns. Two spellings of "which group is this" put a slider's baht in one slot and draw
 * the bar from another, so there is one.
 */
export function groupKeyOf(group, index) {
  const id = group && group.id != null ? String(group.id) : '';
  return id !== '' ? id : `@${index}`;
}

/** One slider's position: the customer's drag if there is one, else what the card shipped. */
export function sliderValue(item, local) {
  if (!item) return 0;
  const dragged = local ? local[item.id] : undefined;
  if (dragged == null) return Number(item.value) || 0;
  const n = Number(dragged);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The floor under one slider — the deduction the customer already holds, in baht.
 *
 * 0 when the payload carries no `min` (an older backend, or a card built before the floor
 * existed): no floor at all, every slider free to run to nothing, which is exactly how the
 * card behaved before 2026-08-20 rather than a new failure mode.
 *
 * 🔴 A floor above `max` is kept as authored. It is not clamped here and must not be clamped
 * by a caller either — see the file header.
 */
export function sliderMin(item) {
  const n = Number(item && item.min);
  return Number.isFinite(n) ? n : 0;
}

/** The item's deduction ceiling (`max`), or null when the payload carries none usable. */
export function itemCap(item) {
  const n = Number(item && item.max);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The END of the draggable range: `sliderMax` when the payload authored one, else the ceiling
 * (never below the floor — a floor above the ceiling is a real card, see the file header).
 *
 * 🔴 `sliderMax` is what lets a thumb travel PAST the dashed cap line. Without it the range
 * ends at `max`, exactly as it did before the redesign, so an old payload keeps its old
 * behaviour rather than gaining a zone nobody drew.
 */
export function sliderTop(item) {
  const min = sliderMin(item);
  const sMax = Number(item && item.sliderMax);
  if (Number.isFinite(sMax) && sMax > 0) return Math.max(sMax, min);
  const max = Number(item && item.max);
  return Number.isFinite(max) ? Math.max(max, min) : Infinity;
}

/** Is this bar kept off the screen? Its holding still counts — see the file header. */
export function isHidden(item) {
  return Boolean(item && (item.hidden === true || item.hidden === 'true' || item.hidden === 1));
}

/** The bars a group actually DRAWS. Allocation still reads `group.items` whole. */
export function visibleItems(group) {
  return ((group && group.items) || []).filter((it) => it && !isHidden(it));
}

/**
 * The "ลดหย่อนเพิ่มเติม" line — Σ of the sliders as they stand.
 *
 * 🔴 Summed from the CONTROLS. The card carries no `picker.total` to read any more, and that
 * is the same decision stated on the wire: a server figure would agree the moment a card
 * arrives and diverge the moment one bar is dragged. This line is the customer's own
 * arithmetic about their own input, which is the one figure on the card allowed to move
 * without asking the server.
 */
export function sliderTotal(items, local) {
  return (items || []).reduce((sum, it) => sum + sliderValue(it, local), 0);
}

/** Where one bar stands in baht — bought + added. The figure the "รวมทั้งหมด" box prints. */
export function itemTotal(item, local) {
  return sliderValue(item, local);
}

/** How much of one bar counts: its position, never more than its own ceiling. */
export function itemDeductible(item, local) {
  const total = itemTotal(item, local);
  const cap = itemCap(item);
  return cap === null ? total : Math.min(total, cap);
}

/**
 * How much of a group's shared ceiling the sliders in it are using.
 *
 * 🔴 Each slider counts at most its OWN ceiling — `max`. Over-ceiling baht are already called
 * out by the row's own badge, and letting them inflate the group bar as well charges the same
 * excess to the customer's ceiling twice on one screen. This caps ONE BAR'S WIDTH, nothing the
 * customer owns or sends.
 */
export function groupUsed(group, local) {
  return ((group && group.items) || []).reduce((sum, it) => sum + itemDeductible(it, local), 0);
}

/**
 * How far one slider sits past its own authored ceiling. 0 when it has none.
 *
 * 🔴 This has to be able to fire on a card nobody has touched: a customer already holding more
 * cover than the law allows arrives with `value` (and `min`) above `max`, and that warning is
 * the whole point of showing them the row. Never suppress it by clamping the value first.
 */
export function overLimit(item, local) {
  const max = Number(item && item.max);
  if (!Number.isFinite(max)) return 0;
  return Math.max(0, sliderValue(item, local) - max);
}

/**
 * Has any slider left the position the server last costed?
 *
 * 🔴 This is what marks every SERVER figure on the card stale. A tax bill printed under a
 * slider that has moved past it is a wrong number the customer has every reason to trust,
 * so the answer has to be exact rather than approximate — compare against the seed, i.e. the
 * card's own `value` lifted to its floor, which is precisely "the plan these figures were
 * computed for" AND the position the thumb is actually parked at. Comparing against a raw
 * `value` that sat under its own floor would report a freshly arrived card as already stale.
 */
export function isDirty(items, local) {
  return (items || []).some((it) => sliderValue(it, local) !== seedValue(it));
}

/**
 * The payload of a submission: EVERY slider on screen, as text.
 *
 * 🔴 KEYED BY THE SLIDER'S `id`, which is the deduction's real FieldKey — echoed back exactly
 * as the card sent it, never a label and never rewritten. That is what
 * `TaxSimulationCardV2.ReadPostback` matches against the card's own key list; a key it does
 * not own is dropped, and a `submit` left with none is refused outright.
 *
 * 🔴 Every one, every time — not just the ones that moved. The backend reads an absent key
 * as "this control was not on their screen" and leaves that field alone, which is right for
 * a row gated off and wrong for one the customer deliberately dragged back to zero.
 * Strings because the whole tax stack stores amounts as text (InvariantCulture on both
 * ends); a JSON number here would be the one place a decimal changes shape in transit.
 *
 * 🔴 Lifted to the floor, and NOT capped at the ceiling. Under `min` is a position the range
 * control cannot produce, so it can only be junk, and sending it would tell the backend to
 * write back less cover than the customer already owns. Over `max` is a real answer that must
 * survive the wire untouched — the ceiling is applied by the calc formula server-side, and a
 * second one here could only disagree with it.
 */
export function submissionValues(items, local) {
  const values = {};
  (items || []).forEach((it) => {
    if (it && it.id != null) {
      values[it.id] = String(Math.max(sliderValue(it, local), sliderMin(it)));
    }
  });
  return values;
}

/**
 * Bar width, 0–100. Drawing only.
 *
 * Clamped at both ends, so a figure past the scale paints a FULL bar instead of running off
 * the end of the track. A scale of 0 is not a fraction of anything and reads 0.
 */
export function trackPct(value, max) {
  const m = Number(max);
  if (!Number.isFinite(m) || m <= 0) return 0;
  const v = Number(value);
  return Math.max(0, Math.min(100, ((Number.isFinite(v) ? v : 0) / m) * 100));
}

/** One slider's starting position: what the card shipped, never under its own floor. */
function seedValue(item) {
  return Math.max(Number(item && item.value) || 0, sliderMin(item));
}

/**
 * The starting positions for a freshly arrived card — its own values, by slider id.
 *
 * 🔴 Lifted to `min`: the customer cannot drag below cover they already hold, so a card that
 * somehow shipped `value < min` still has to start ON the floor. A thumb parked outside its
 * own range is a control the customer can move but never put back where they found it.
 * Nothing is lifted DOWN to `max` — a seed above the ceiling is the over-ceiling case working.
 */
export function seedValues(items) {
  const next = {};
  (items || []).forEach((it) => {
    if (it && it.id != null) next[it.id] = seedValue(it);
  });
  return next;
}

/**
 * Every slider parked on its floor — "the plan before this screen", by id.
 *
 * 🔴 This is what "ภาษีก่อนวางแผน" is measured against: the customer's holdings with nothing
 * added, run through the SAME allocation the live plan goes through, so the two figures the
 * saving is the difference of are computed one way.
 */
export function floorValues(groups) {
  const next = {};
  (groups || []).forEach((g) => {
    ((g && g.items) || []).forEach((it) => {
      if (it && it.id != null) next[it.id] = sliderMin(it);
    });
  });
  return next;
}

// ══════════════════════════════════════════════════════════════════════════════════════════
//  relation_cap — one slider filling ANOTHER group's ceiling before its own
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * 🔴 WHY THIS EXISTS. Some deductions are written in law as "you may use whatever is left of
 * THAT ceiling first, and only the remainder counts against your own" — a pension premium
 * against the life-insurance 100,000, for instance. Drawn naively, the same baht then appears
 * under two ceilings on one screen and the customer is shown more headroom than they have.
 *
 * The wire says it with one key: `relation_cap` on the item names the `id` of the group it
 * fills FIRST. Everything below is the arithmetic of that sentence and nothing else — no tax
 * rule, no ceiling that decides money. The ceilings that decide tax live in the calc formulas
 * and are applied server-side; these decide how wide a bar is drawn and what the caption over
 * it says — and, since the redesign, what the live saving PREVIEW reads.
 *
 * THE RULE, in the order it resolves:
 *   1. Every group first spends its OWN items against its own ceiling — each item counted at
 *      most at its OWN ceiling (`max`) before anything reaches the group's.
 *   2. A borrower may then take only the headroom those own items LEFT — never more, and
 *      never at the expense of the group it is borrowing from.
 *   3. Whatever will not fit goes home and counts against the borrower's own ceiling.
 *
 * That ordering is what makes the split move the way the spec describes: 5,000 lent into a
 * 100,000 ceiling is 5,000 lent while that group holds 95,000 of its own, and becomes 2,000
 * lent + 3,000 at home the moment its own items grow by 3,000. Nothing is ever counted twice.
 *
 * 🔴 A TARGET WITH NO CEILING LENDS NOTHING. "Fill it up first" is meaningless without a
 * ceiling to fill, and treating it as infinite would move every borrowed baht into a group
 * that draws no bar — the money would simply vanish from the screen. Same for a
 * `relation_cap` naming the item's own group or a group that is not on the card: ignored, the
 * item stays home. Ignoring is always the safe direction here, because home is where the
 * amount was already going to be counted.
 */

/** What one slider contributes to any ceiling: its position, never more than its own. */
function counted(item, local) {
  return itemDeductible(item, local);
}

/** The locked part of that contribution — cover the customer already holds. */
function countedBase(item, local) {
  return Math.min(sliderMin(item), counted(item, local));
}

/**
 * Where every slider's baht actually lands, group by group.
 *
 * Returns `{ [groupKey]: slot }` where a slot is
 * `{ key, cap, used, deductible, over, rawAll, bought, lent, home, contributions[] }`:
 *   `used`        — Σ of what landed in this ceiling, every item counted at its own `max`.
 *   `deductible`  — `used`, never more than `cap` (= `used` for a group with no cap).
 *   `rawAll`      — Σ of what landed here UNCAPPED (bought + added of the own items that
 *                   stayed home, plus every borrowed slice). The figure the over-zone reads.
 *   `over`        — `rawAll − cap` when positive: what this group is being asked to hold past
 *                   its ceiling. Includes an item's own excess (18,000 of parental cover in a
 *                   15,000 group is 3,000 over, even though the group cap was never exceeded
 *                   by the capped figure).
 *   `bought`      — Σ locked cover in the bar (own floors + the locked part of borrowed
 *                   slices), never more than `cap`. The dark slice.
 *   `lent`        — what THIS group's own items lent INTO other ceilings.
 *   `home`        — what those lending items kept at home.
 *   `contributions` — `{ itemId, fromGroup, amount, base }` in drawing order: the group's own
 *                   items first, in payload order, then the borrowed slices in the order their
 *                   groups appear. `fromGroup` is the group the slider BELONGS to.
 *
 * 🔴 Hidden items take part exactly like drawn ones — a provident fund the customer cannot
 * top up here still eats the retirement ceiling.
 */
export function allocate(groups, local) {
  const list = (groups || []).filter(g => g && Array.isArray(g.items));
  const byKey = new Map();
  list.forEach((g, gi) => {
    const key = groupKeyOf(g, gi);
    const cap = Number(g.cap);
    byKey.set(key, {
      key,
      cap: Number.isFinite(cap) ? cap : null,
      own: 0,
      rawAll: 0,
      lent: 0,
      home: 0,
      contributions: [],
    });
  });

  const borrowers = [];

  // Pass 1 — every group spends its own items first. A borrower is set aside, not counted.
  list.forEach((g, gi) => {
    const key = groupKeyOf(g, gi);
    const slot = byKey.get(key);
    (g.items || []).forEach((it) => {
      const amount = counted(it, local);
      const raw = Math.max(0, itemTotal(it, local));
      const target = it && it.relation_cap != null ? String(it.relation_cap) : '';
      const lends = target !== '' && target !== key && byKey.has(target)
        && byKey.get(target).cap !== null;
      if (lends) {
        borrowers.push({ item: it, fromGroup: key, target, amount, raw, base: countedBase(it, local) });
        return;
      }
      slot.own += amount;
      slot.rawAll += raw;
      slot.contributions.push({
        itemId: it && it.id, fromGroup: key, amount, base: countedBase(it, local),
      });
    });
  });

  // Pass 2 — borrowers take what the target's OWN items left, in payload order, and carry the
  // remainder home. 🔴 `headroom` is recomputed from `own` as it stands after pass 1 and then
  // decremented per borrower, so two sliders pointing at one ceiling share it instead of both
  // seeing it empty.
  const headroom = new Map();
  byKey.forEach((slot, key) => {
    headroom.set(key, slot.cap === null ? 0 : Math.max(0, slot.cap - slot.own));
  });

  borrowers.forEach((b) => {
    const room = headroom.get(b.target) || 0;
    const lent = Math.max(0, Math.min(b.amount, room));
    headroom.set(b.target, room - lent);

    const home = byKey.get(b.fromGroup);
    home.lent += lent;
    if (lent > 0) {
      const target = byKey.get(b.target);
      target.rawAll += lent;
      target.contributions.push({
        itemId: b.item && b.item.id,
        fromGroup: b.fromGroup,          // ← its OWN group's colour, in someone else's bar
        amount: lent,
        base: Math.min(b.base, lent),
      });
    }
    const kept = b.amount - lent;
    home.own += kept;
    home.home += kept;
    // What stays home UNCAPPED: the item's whole position less what was lent away.
    home.rawAll += Math.max(0, b.raw - lent);
    if (kept > 0) {
      home.contributions.push({
        itemId: b.item && b.item.id,
        fromGroup: b.fromGroup,
        amount: kept,
        base: Math.max(0, b.base - lent),
      });
    }
  });

  const out = {};
  byKey.forEach((slot, key) => {
    const used = slot.contributions.reduce((s, c) => s + c.amount, 0);
    const bought = slot.contributions.reduce((s, c) => s + Math.max(0, c.base), 0);
    out[key] = {
      // 🔴 The group's OWN key travels with the slot, because `capSegments` has to tell
      // "my own item" from "someone else's item lent into me" and a contribution only
      // states where it CAME from. Without it the bar cannot draw the three colours the
      // app draws — a borrowed slice would be indistinguishable from a home one.
      key,
      cap: slot.cap,
      used,
      deductible: slot.cap === null ? used : Math.min(used, slot.cap),
      rawAll: slot.rawAll,
      over: slot.cap === null ? 0 : Math.max(0, slot.rawAll - slot.cap),
      bought: slot.cap === null ? bought : Math.min(bought, slot.cap),
      lent: slot.lent,
      home: slot.home,
      contributions: slot.contributions,
    };
  });
  return out;
}

/** The slot one group owns in an `allocate` result — by `id`, else by its index. */
export function groupSlot(alloc, group, index) {
  const key = groupKeyOf(group, index);
  return (alloc && alloc[key]) || {
    key, cap: null, used: 0, deductible: 0, rawAll: 0, over: 0, bought: 0, lent: 0, home: 0, contributions: [],
  };
}

/**
 * The stacked ceiling bar, as widths in percent — AT MOST THREE SLICES, in this order:
 *
 *   1. `base`     — the locked `min` of this group's OWN items. The bar opens with what the
 *                   group has already committed, so anything added grows out of the end of
 *                   that block instead of from zero.
 *   2. `own`      — what those same items added on top.
 *   3. `relation` — everything lent IN through `relation_cap`, all lenders merged.
 *
 * Kept for the older three-colour rendering; the redesign draws the group bar from
 * `groupBar` instead. A zero slice is dropped, widths are shares of the ceiling, clamped so
 * the whole stack never exceeds the track.
 */
export function capSegments(slot) {
  const cap = slot && Number.isFinite(Number(slot.cap)) ? Number(slot.cap) : 0;
  if (cap <= 0) return [];

  const home = slot.key == null ? '' : String(slot.key);
  let base = 0;
  let own = 0;
  let relation = 0;
  (slot.contributions || []).forEach((c) => {
    if (String(c.fromGroup) === home) {
      base += Math.max(0, c.base);
      own += Math.max(0, c.amount - c.base);
    } else {
      relation += Math.max(0, c.amount);
    }
  });

  let left = 100;
  const out = [];
  [{ kind: 'base', amount: base }, { kind: 'own', amount: own }, { kind: 'relation', amount: relation }]
    .forEach((r) => {
      if (left <= 0 || r.amount <= 0) return;
      const pct = Math.min(left, (r.amount / cap) * 100);
      if (pct <= 0) return;
      out.push({ kind: r.kind, pct });
      left -= pct;
    });
  return out;
}

/**
 * The redesign's group bar (`.gtrack` in the design): three slices laid end to end on a scale
 * that runs a little past whichever is bigger, the ceiling or what the group holds.
 *
 *   `bought`  — locked cover already in the bar (dark), never past the ceiling
 *   `planned` — what the plan adds up to the ceiling (the group's identity colour)
 *   `over`    — what is being held past the ceiling (the striped over-zone)
 *   `capPct`  — where the ceiling sits on the scale; the tinted zone runs from here to the end
 *
 * null for a group with no ceiling — there is no bar to draw.
 */
export function groupBar(slot, headroom) {
  if (!slot || slot.cap === null || !(Number(slot.cap) > 0)) return null;
  const cap = Number(slot.cap);
  const h = Number(headroom);
  const scale = Math.max(cap, slot.rawAll || 0) * (Number.isFinite(h) && h > 0 ? h : GROUP_BAR_HEADROOM);
  const bought = Math.min(slot.bought || 0, cap);
  const planned = Math.max(0, (slot.deductible || 0) - bought);
  const over = Math.max(0, slot.over || 0);
  return {
    scale,
    capPct: trackPct(cap, scale),
    boughtPct: trackPct(bought, scale),
    plannedPct: trackPct(planned, scale),
    overPct: trackPct(over, scale),
    bought,
    planned,
    over,
  };
}

/** Σ over every group of what counts — the DEDUCTIBLE tile. */
export function deductibleTotal(groups, local) {
  const alloc = allocate(groups, local);
  return Object.keys(alloc).reduce((sum, k) => sum + alloc[k].deductible, 0);
}

/** Σ of what the customer ADDED on the bars they can see — the NEW_MONEY tile. */
export function newMoneyTotal(groups, local) {
  return (groups || []).reduce((sum, g) => sum + addedTotal(visibleItems(g), local), 0);
}

// ══════════════════════════════════════════════════════════════════════════════════════════
//  the live preview — `picker.tax` shipped by the server
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * The ladder as usable rows: `{ upTo: number|null, rate: number }`, in payload order.
 *
 * 🔴 ALL OR NOTHING. A row without a readable `rate` voids the whole ladder rather than being
 * skipped: dropping one step shifts every step above it down, and the preview would then walk
 * a ladder nobody authored and print a saving that is confidently wrong. No figure (an em
 * dash on the tile) is the honest answer to a ladder the server did not ship whole.
 */
function bracketRows(brackets) {
  if (!Array.isArray(brackets) || !brackets.length) return [];
  const rows = [];
  for (const b of brackets) {
    if (!b || typeof b !== 'object') return [];
    const rate = Number(b.rate);
    if (!Number.isFinite(rate) || rate < 0) return [];
    const upTo = b.upTo == null ? null : Number(b.upTo);
    rows.push({ upTo: upTo == null || !Number.isFinite(upTo) ? null : upTo, rate });
  }
  return rows;
}

/**
 * Tax on a net income, walked up the ladder the server shipped — MARGINAL, `rate` a fraction
 * (0.05), a bracket with no `upTo` the open top one. Whole baht, never negative.
 *
 * 🔴 A PREVIEW, not the return. The formulas cost the plan server-side on submit; this is what
 * lets the saving tile move while the thumb does. The ladder is the server's, never typed here.
 */
export function taxFromBrackets(net, brackets) {
  const rows = bracketRows(brackets);
  const n = Number(net);
  if (!rows.length || !Number.isFinite(n) || n <= 0) return 0;
  let tax = 0;
  let prev = 0;
  for (const row of rows) {
    if (n <= prev) break;
    const hi = row.upTo === null ? Infinity : row.upTo;
    const slice = Math.min(n, hi) - prev;
    if (slice > 0) tax += slice * row.rate;
    prev = hi;
    if (n <= hi) break;
  }
  return Math.max(0, Math.round(tax));
}

/** The rate of the highest bracket a net income reaches — 0 when it reaches none. */
export function topRateFromBrackets(net, brackets) {
  const rows = bracketRows(brackets);
  const n = Number(net);
  if (!rows.length || !Number.isFinite(n)) return 0;
  let last = 0;
  let prev = 0;
  for (const row of rows) {
    if (n > prev) last = row.rate;
    prev = row.upTo === null ? Infinity : row.upTo;
  }
  return last;
}

/** Is `picker.tax` something the preview can read? Both halves, or nothing. */
export function usableTax(tax) {
  if (!tax || typeof tax !== 'object') return null;
  const net = Number(tax.netIncomeBeforeProducts);
  const rows = bracketRows(tax.brackets);
  if (!Number.isFinite(net) || net < 0 || !rows.length) return null;
  return { net, brackets: rows };
}

/**
 * The TAX_SAVED tile: the tax on the plan BEFORE this screen minus the tax on the plan as it
 * stands, both measured off the same net income and through the same allocation.
 *
 * null when the server shipped no usable `picker.tax` — an em dash on screen, never a 0 that
 * would read as "this plan saves nothing".
 */
export function taxSaved(tax, groups, local) {
  const t = usableTax(tax);
  if (!t) return null;
  const before = deductibleTotal(groups, floorValues(groups));
  const now = deductibleTotal(groups, local);
  const taxBefore = taxFromBrackets(t.net - before, t.brackets);
  const taxNow = taxFromBrackets(t.net - now, t.brackets);
  return taxBefore - taxNow;
}

// ══════════════════════════════════════════════════════════════════════════════════════════
//  one row's geometry — the scale a bar is drawn on and where each piece of it sits
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * The scale one bar is drawn on (the design's `scaleMax`): a little past the ceiling or the
 * holding, whichever is taller, and never so short that the thumb falls off the end.
 */
export function scaleMax(item, local, opts) {
  const o = opts || {};
  const sh = Number(o.scaleHeadroom);
  const dh = Number(o.dragHeadroom);
  const scaleHeadroom = Number.isFinite(sh) && sh > 0 ? sh : DEFAULT_SCALE_HEADROOM;
  const dragHeadroom = Number.isFinite(dh) && dh > 0 ? dh : DEFAULT_DRAG_HEADROOM;
  const cap = itemCap(item) || 0;
  const bought = sliderMin(item);
  const total = itemTotal(item, local);
  const scale = Math.max(Math.max(cap, bought) * scaleHeadroom, total * dragHeadroom);
  return scale > 0 ? scale : 1;
}

/**
 * Everything the row's track needs, as percentages of `scaleMax`, straight from the design's
 * `render()`:
 *   bought    — the dark slice, up to the ceiling
 *   boughtOver — the part of the holding already past the ceiling (striped)
 *   plan      — what was added, up to the ceiling (identity colour)
 *   planOver  — what was added past the ceiling (striped)
 *   cap       — the dashed line; `capFlagAlign` keeps its label on the track, `capFlagVisible`
 *               hides it when the line sits so far left it would collide with the caption
 *   thumb / tag — centred on the total; the tag is kept a little inside both ends
 *   over / locked — the thumb's state; `divider` — the white tick between bought and added
 */
export function rowGeometry(item, local, opts) {
  const S = scaleMax(item, local, opts);
  const cap = itemCap(item);
  const P = sliderMin(item);
  const A = addedValue(item, local);
  const T = P + A;
  const pct = (v) => trackPct(v, S);

  const capEnd = cap === null ? S : Math.min(cap, S);
  const pEnd = Math.min(P, S);
  const tEnd = Math.min(T, S);
  const pOk = Math.min(pEnd, capEnd);
  const sEnd = Math.max(pEnd, Math.min(tEnd, capEnd));
  const oStart = Math.max(pEnd, capEnd);
  const oEnd = Math.max(oStart, tEnd);
  const capRatio = capEnd / S;

  return {
    scale: S,
    bought: P,
    added: A,
    total: T,
    cap,
    boughtPct: pct(pOk),
    boughtOverLeft: pct(pOk),
    boughtOverPct: pEnd > capEnd ? pct(pEnd - pOk) : 0,
    planLeft: pct(pEnd),
    planPct: pct(Math.max(0, sEnd - pEnd)),
    planOverLeft: pct(oStart),
    planOverPct: pct(Math.max(0, oEnd - oStart)),
    capPct: cap === null ? 100 : pct(capEnd),
    capFlagAlign: capRatio > 0.8 ? 'end' : 'center',
    capFlagVisible: cap !== null && capRatio >= 0.34,
    thumbPct: pct(tEnd),
    tagPct: Math.min(97, Math.max(3, pct(tEnd))),
    over: cap !== null && T > cap,
    locked: cap !== null && A === 0 && P >= cap,
    divider: P > 0 && A > 0,
  };
}

// ══════════════════════════════════════════════════════════════════════════════════════════
//  the boxes under a bar
// ══════════════════════════════════════════════════════════════════════════════════════════

/**
 * What the editable box shows: the INCREASE over the locked floor, never the absolute figure.
 *
 * 🔴 The customer is being asked "how much more would you buy", and a box that opened at the
 * 40,000 they already hold answers a different question — the first keystroke would replace a
 * number they never chose to state.
 *
 * Capped at the END OF THE RANGE (`sliderTop`): the ceiling when the payload authored no
 * `sliderMax`, past it up to `sliderMax` when it did. The box agrees with the bar under it
 * either way.
 */
export function addedValue(item, local) {
  const min = sliderMin(item);
  const value = sliderValue(item, local);
  const top = sliderTop(item);
  return Math.max(0, Math.min(value, top) - min);
}

/**
 * The "รวมลดหย่อนเพิ่มเติม" line — Σ of what the customer has ADDED, never Σ of where the
 * thumbs sit.
 *
 * 🔴 This line answers the question the screen asks ("how much MORE would you buy"), so the
 * cover they arrived holding is not part of it. Summing the absolute positions instead states
 * their whole existing plan under a heading that promises the increase, which reads as tens of
 * thousands of baht of new deduction they never chose.
 *
 * 🔴 Group ceilings do NOT reduce it: an over-ceiling group still contributes its excess even
 * though the warning under it says that excess is not deductible. That is what the app does
 * today; if it ever changes, every platform has to change with it.
 */
export function addedTotal(items, local) {
  return (items || []).reduce((sum, it) => sum + addedValue(it, local), 0);
}

/** One row's snap granularity. `< 1`, absent or unreadable → 1, so a commit always lands. */
export function stepOf(item) {
  const n = Number(item && item.step);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * A typed increase, COMMITTED: snapped to the row's `step` and held inside its range.
 *
 * 🔴 Snapping happens on commit only — never per keystroke. Rewriting the box mid-word fights
 * the caret ("2" snapped to "500" the moment it is typed), which is why `valueFromAdded` takes
 * the figure raw and this is the one that rounds it.
 */
export function snapAdded(item, typed) {
  const step = stepOf(item);
  const n = Number(typed);
  const added = Number.isFinite(n) ? Math.max(0, n) : 0;
  return valueFromAdded(item, Math.round(added / step) * step);
}

/** A slider position, snapped to `step` and held inside `min…sliderTop`. The drag's own arithmetic. */
export function snapValue(item, value) {
  const step = stepOf(item);
  const min = sliderMin(item);
  const n = Number(value);
  const snapped = Math.round((Number.isFinite(n) ? n : 0) / step) * step;
  const top = sliderTop(item);
  return Math.max(min, Math.min(snapped, Number.isFinite(top) ? top : snapped));
}

/**
 * The slider position an increase typed into that box means: floor + increase, held inside the
 * range the control can actually reach. 🔴 Clamped to the range's END here — unlike a
 * submission, which must carry an over-ceiling figure through untouched — because this is the
 * position of a control, and a thumb cannot stand past the end of its own track.
 */
export function valueFromAdded(item, typed) {
  const min = sliderMin(item);
  const n = Number(typed);
  const added = Number.isFinite(n) ? Math.max(0, n) : 0;
  const value = min + added;
  const top = sliderTop(item);
  return Number.isFinite(top) ? Math.min(value, top) : value;
}

/** Is this row's box typable? Absent `editable` means yes — the app's own default. */
export function isEditable(item) {
  return !(item && item.editable === false);
}

/**
 * The status pill beside a heading — `{ kind, text }`, or null for a row/group with no
 * ceiling to measure against.
 *
 *   over      — the total is PAST the ceiling: "เกินสิทธิ์ {amount}" with the excess
 *   full      — exactly on it: "เต็มสิทธิ์แล้ว"
 *   remaining — under it: "เหลือสิทธิลดหย่อน {amount}" with the room left
 *
 * `badge` is `picker.badge` (may be absent — the design's Thai captions are the fallback);
 * `fmt` formats the amount, and is the card's own formatter so the pill agrees with every
 * other figure on screen.
 */
export function badgeFor(total, cap, badge, fmt) {
  const c = Number(cap);
  if (!Number.isFinite(c) || c <= 0) return null;
  const t = Number(total);
  const T = Number.isFinite(t) ? t : 0;
  const b = badge && typeof badge === 'object' ? badge : {};
  const label = (key) => {
    const own = b[key];
    return own == null || String(own).trim() === '' ? DEFAULT_BADGE[key] : String(own);
  };
  const format = typeof fmt === 'function' ? fmt : (v) => String(v);
  if (T > c) return { kind: 'over', text: fillNote(label('overLabel'), { amount: format(T - c) }) };
  if (T === c) return { kind: 'full', text: fillNote(label('fullLabel'), { amount: format(0) }) };
  return { kind: 'remaining', text: fillNote(label('remainingLabel'), { amount: format(c - T) }) };
}

/**
 * `{used}` / `{cap}` / `{excess}` / `{lent}` / `{home}` / `{amount}` filled in from the live
 * figures.
 *
 * 🔴 The caller passes ALREADY-FORMATTED strings. Formatting is the card's job (thousands
 * separators come from the same helper every other amount on the card uses), and a second
 * formatter in here would eventually disagree with it on one screen.
 */
export function fillNote(text, values) {
  const src = String(text == null ? '' : text);
  if (!src) return '';
  const v = values || {};
  const put = (s, key) => s.replace(new RegExp(`\\{${key}\\}`, 'g'), String(v[key] == null ? '' : v[key]));
  return ['used', 'cap', 'excess', 'lent', 'home', 'amount'].reduce(put, src);
}
